import { useEffect, useState } from 'react';

import { Button } from '@mui/material';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';

import InputForm from 'shared/components/Input/InputForm';
import Loader from 'shared/containers/Loader';
import { APIErrorT } from 'shared/models/api';
import { SCHCTemplateStackT } from 'shared/models/schc-templates';
import SCHCTemplate from 'shared/models/SCHCTemplate';
import api from 'utils/api';

import AccordionLayout from './components/AccordionLayout';
import SCHCTemplatesRuleForm from './components/SCHCTemplatesRuleForm';

interface FieldRuleArrayItem extends SCHCTemplate.Form.RuleT {
  id?: string;
}
function SCHCTemplatesManageDataRules() {
  const {
    getValues,
    register,
    formState: {
      errors,
    },
  } = useFormContext();
  const [stacksAvailable, setStacksAvailable] = useState<Array<SCHCTemplateStackT>>([]);
  const {
    fields, append, remove, move,
  } = useFieldArray({ name: 'rules' });
  const [isLoading, setIsLoading] = useState(true);

  /* ===== Handle display #RuleId ===== */
  const [forceRerenderAccordionLayout, setForceRerenderAccordionLayout] = useState(0);

  useEffect(() => {
    api.SCHCTemplates.getStacks()
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
              setIsLoading(false);
            });
        } else {
          resp.json()
            .then((data: any) => {
              setStacksAvailable(data.result);
              setIsLoading(false);
            });
        }
      });
  }, []);

  const deleteRule = (index: number) => {
    if (window.confirm(`Do you want to remove the rule at the position ${index + 1} ?`)) {
      remove(index);
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (result.destination) {
      const srcIndex = result.source.index;
      const destIndex = result.destination.index;
      if (srcIndex !== destIndex) {
        toast.info(`Rule #${(getValues().rules[srcIndex] as FieldRuleArrayItem).ruleId} moved`);
        move(srcIndex, destIndex);
      }
    }
  };

  const addRule = () => {
    append({
      // New ruleId: last ruleId + 1
      ruleId: fields.length ? `${+(fields[fields.length - 1] as FieldRuleArrayItem).ruleId + 1}` : '1',
      ruleType: SCHCTemplate.COMPRESSION_RULE,
    } as FieldRuleArrayItem);
  };

  if (isLoading) {
    return <Loader.Section />;
  }
  return (
    <>
      <InputForm.TextField
        fullWidth
        sx={{ mt: 1, mb: 1, pr: 1.5 }}
        label="Non-matching Rule ID"
        register={register('nonMatchingRule')}
        errorField={errors.nonMatchingRule}
        helperText="Default Rule ID used when the packet can't be compressed"
      />
      <div className="title__header">
        <div className="title__header--block">
          <h2 className={`${errors.rules ? 'title-error' : ''}`}>Rules</h2>
        </div>
        <div className="title__header--actions">
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={addRule}
          >
            Add
          </Button>
        </div>
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(dropProvided) => (
            <div
              {...dropProvided.droppableProps}
              ref={dropProvided.innerRef}
              className="scroll"
            >
              {fields.map((field, index) => (
                <Draggable key={field.id} draggableId={field.id} index={index}>
                  {(dragProvided, dragSnapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                    >
                      <AccordionLayout
                        // Issue on id index value when you remove one item
                        // and the id hasn't been edited by the user
                        index={index}
                        forceRerender={forceRerenderAccordionLayout}
                        key={field.id}
                        onDelete={() => deleteRule(index)}
                        hasError={Array.isArray(errors.rules) && !!errors.rules[index]}
                        isDragging={dragSnapshot.isDragging}
                      >
                        <SCHCTemplatesRuleForm
                          index={index}
                          stacksAvailable={stacksAvailable}
                          onChangeRuleId={() => (
                            setForceRerenderAccordionLayout((count) => count + 1)
                          )}
                        />
                      </AccordionLayout>
                    </div>
                  )}
                </Draggable>
              ))}
              {dropProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
}

export default SCHCTemplatesManageDataRules;
